import { fakePromiseDelay, useAppSelector, useAppTranslation } from "@/utils";
import { useCountDown } from "@/utils/hooks/useCountdown";
import { bnplConsumerApis } from "@payping/api";
import type { SDKRequestInit } from "@payping/api";
import { Button, Flex, InputNumber, LegacyForm, Typography, useMessagedAsyncFn } from "@payping/web-components";
import type { FormProps } from "@payping/web-components/legacyComponents/Form/types";
import PayGetOrderLayout from "../Layout";

type FieldType = {
  code: number;
};

interface ConfirmCodeProps {
  onFinish: () => void;
  trackingCode: string;
  unknownFinish: () => void;
}
const PayGetOrderCreditScoreConfirmCode = (props: ConfirmCodeProps) => {
  const { t } = useAppTranslation();

  const profile = useAppSelector((state) => state.bnplConsumer.profile);

  const [form] = LegacyForm.useForm<FieldType>();

  const { format, timeLeft, reset } = useCountDown(new Date(new Date().getTime() + 1.8 * 60 * 1000));

  const [{ loading: resendLoading }, resendCode] = useMessagedAsyncFn(async () => {
    try {
      const res = await bnplConsumerApis.creditScoreValidationOTP({ trackingCode: props.trackingCode }, {
        isRaw: true,
      } as SDKRequestInit);
      reset(new Date(Date.now() + 1.8 * 60 * 1000));
      return res;
    } catch (e: any) {
      // Check if e is an instance of Response and if the status is 400
      if (e.status === 400) {
        try {
          const parsedError = await e.json();
          // report did not success
          if ((parsedError as any)?.key === 112) {
            await fakePromiseDelay(10000);
            props.unknownFinish();
            return undefined;
          }
          throw parsedError;
        } catch (jsonError: any) {
          if (jsonError.status === 400) {
            throw jsonError;
          }
          throw e; // Fallback if parsing JSON fails
        }
      }
      throw e;
    }
  }, [props, reset]);

  const [{ loading: verifyLoading }, verify] = useMessagedAsyncFn(
    async (code: string) => {
      try {
        const res = await bnplConsumerApis.creditScoreVerify({ trackingCode: props.trackingCode, code }, {
          isRaw: true,
        } as SDKRequestInit);
        props.onFinish();
        return res;
      } catch (e: any) {
        // Check if e is an instance of Response and if the status is 400
        if (e.status === 400) {
          try {
            const parsedError = await e.json();
            // report did not success
            if ((parsedError as any)?.key === 112) {
              await fakePromiseDelay(10000);
              props.unknownFinish();
              return undefined;
            }
            throw parsedError;
          } catch (jsonError: any) {
            if (jsonError.status === 400) {
              if (jsonError.key === 101 && jsonError.errors instanceof Array && jsonError.errors?.[0]?.message) {
                throw new Error(jsonError.errors?.[0]?.message, { cause: jsonError });
              }
              throw jsonError;
            }
            throw e; // Fallback if parsing JSON fails
          }
        }
        throw e;
      }
    },
    [props],
    { loading: false },
    { error: true },
  );

  const handleResendCode = () => {
    if (timeLeft <= 0) {
      resendCode();
    }
  };

  const onFinish: FormProps<FieldType>["onFinish"] = (values) => {
    verify(values.code.toString());
  };

  return (
    <PayGetOrderLayout showHeaders>
      <Flex vertical className="tw:h-full tw:w-full tw:px-4! tw:pb-4! tw:pt-6!" justify="center" gap={"small"}>
        <Flex gap={"small"} vertical>
          <Typography.Title level={5} className="tw:m-0! tw:font-semibold!">
            {t("pay.getOrder.steps.creditScore.confirmCodeTitle")}
          </Typography.Title>
          <Typography.Paragraph className="tw:m-0!" type="secondary">
            {t("pay.getOrder.steps.creditScore.confirmCodeContent", { number: profile.mobileNumber ?? "" })}
          </Typography.Paragraph>
          <LegacyForm.Provider>
            <LegacyForm<FieldType> form={form} onFinish={onFinish}>
              <Flex vertical gap={"large"}>
                <LegacyForm.Item name={"code"} rules={[{ required: true }]} className="tw:m-0">
                  <InputNumber inputMode="numeric" changeOnWheel={false} controls={false} className="tw:w-full!" />
                </LegacyForm.Item>
                <Button
                  disabled={timeLeft > 0 || verifyLoading}
                  loading={resendLoading}
                  onClick={() => {
                    handleResendCode();
                  }}
                  type="link"
                >
                  {`${t("pay.getOrder.steps.creditScore.resendConfirmCode")} ${timeLeft > 0 ? format : ""}`}
                </Button>
              </Flex>
            </LegacyForm>
          </LegacyForm.Provider>
        </Flex>
        <Flex align="center" className="tw:mt-auto tw:w-full" gap={"middle"}>
          <Button
            block
            className="tw:w-full"
            disabled={resendLoading}
            loading={verifyLoading}
            type="primary"
            onClick={() => {
              form.submit();
            }}
          >
            {t("common.confirm")}
          </Button>
        </Flex>
      </Flex>
    </PayGetOrderLayout>
  );
};

export default PayGetOrderCreditScoreConfirmCode;
